import React, { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  CalendarOff,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  getTherapistAways,
  createTherapistAway,
  updateTherapistAway,
  deleteTherapistAway,
} from "../../lib/therapistApi";

// Time slot generation and helper functions
const pad = (n) => n.toString().padStart(2, "0");
const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};
const addMinutes = (hhmm, mins) => {
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + mins;
  const H = Math.floor(total / 60);
  const M = total % 60;
  return `${pad(H)}:${pad(M)}`;
};
const minutesBetween = (start, end) => {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return eh * 60 + em - (sh * 60 + sm);
};

// Generate time slots (30-minute intervals from 7 AM to 9 PM)
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 7; hour <= 21; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      slots.push(`${pad(hour)}:${pad(minute)}`);
    }
  }
  return slots;
};

const timeSlots = generateTimeSlots();

function useTherapistId() {
  const { therapistId } = useAuth();
  return therapistId;
}

const defaultForm = {
  therapistId: "",
  startTimeSlot: "09:00",
  endTimeSlot: "17:00",
  from: "",
  to: "",
  type: "break",
  duration: 480, // 8 hours in minutes
  note: "",
  repeat: "no-repeat",
};

const AvailabilityManagement = () => {
  const therapistId = useTherapistId();
  const [aways, setAways] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAwayId, setCurrentAwayId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  const loadAways = useCallback(async () => {
    if (!therapistId) {
      console.warn("No therapist ID available");
      return;
    }

    try {
      setLoading(true);
      const data = await getTherapistAways();
      // Filter aways to only show those for the current therapist
      const filteredAways = Array.isArray(data)
        ? data.filter((away) => away.therapistId === therapistId)
        : [];
      setAways(filteredAways);
    } catch (error) {
      toast.error(error?.message || "Failed to load away periods");
      setAways([]);
    } finally {
      setLoading(false);
    }
  }, [therapistId]);

  useEffect(() => {
    if (therapistId) {
      loadAways();
    }
  }, [loadAways, therapistId]);

  const handleOpenDialog = (away = null) => {
    if (away) {
      setEditMode(true);
      setCurrentAwayId(away._id);
      setForm({
        therapistId: away.therapistId || therapistId,
        startTimeSlot: away.startTimeSlot || "09:00",
        endTimeSlot: away.endTimeSlot || "17:00",
        from: away.from || "",
        to: away.to || "",
        type: away.type || "break",
        duration: away.duration || 480,
        note: away.note || "",
        repeat: away.repeat || "no-repeat",
      });
    } else {
      setEditMode(false);
      setCurrentAwayId(null);
      const today = new Date().toISOString().split("T")[0];
      setForm({
        ...defaultForm,
        therapistId,
        from: `${today}T09:00:00`,
        to: `${today}T17:00:00`,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setForm(defaultForm);
    setEditMode(false);
    setCurrentAwayId(null);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.from ||
      !form.to ||
      !form.type ||
      !form.startTimeSlot ||
      !form.endTimeSlot
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate date range
    const fromDate = new Date(form.from);
    const toDate = new Date(form.to);
    if (fromDate > toDate) {
      toast.error("End date must be after start date");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        therapistId: form.therapistId || therapistId,
        startTimeSlot: form.startTimeSlot,
        endTimeSlot: form.endTimeSlot,
        from: new Date(form.from).toISOString(),
        to: new Date(form.to).toISOString(),
        type: form.type,
        duration:
          form.duration ||
          Math.round((toDate.getTime() - fromDate.getTime()) / (1000 * 60)), // minutes
        note: form.note || "",
        repeat: form.repeat || "no-repeat",
      };

      if (editMode && currentAwayId) {
        await updateTherapistAway(currentAwayId, payload);
        toast.success("Away period updated successfully");
      } else {
        await createTherapistAway(payload);
        toast.success("Away period created successfully");
      }

      await loadAways();
      handleCloseDialog();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save away period",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (awayId, awayTherapistId) => {
    if (!window.confirm("Are you sure you want to delete this away period?"))
      return;

    try {
      await deleteTherapistAway(awayId, { therapistId: awayTherapistId });
      toast.success("Away period deleted successfully");
      await loadAways();
    } catch (error) {
      toast.error(error?.message || "Failed to delete away period");
    }
  };

  const getTypeBadge = (type) => {
    const config = {
      break: { label: "Break", variant: "default" },
      unavailable: { label: "Unavailable", variant: "secondary" },
      "sick day": { label: "Sick Day", variant: "destructive" },
      holiday: { label: "Holiday", variant: "outline" },
    };
    const item = config[type] || config.break;
    return <Badge variant={item.variant}>{item.label}</Badge>;
  };

  const getDuration = (from, to) => {
    const fromDate = parseISO(from);
    const toDate = parseISO(to);
    const days = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24));
    return days === 1 ? "1 day" : `${days} days`;
  };

  if (!therapistId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-gray-500">
          <CalendarOff className="h-6 w-6 text-indigo-400" />
          <p>
            Unable to load availability management. Please ensure you're logged
            in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            Availability Management
          </h1>
          <p className="text-slate-600 mt-2">
            Manage your away periods, vacations, and time off
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadAways}
            disabled={loading}
            className="border-slate-300 text-slate-600 hover:bg-slate-50">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4" />
            )}
            <span className="hidden sm:inline ml-2">Refresh</span>
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => handleOpenDialog()}
                className="gap-2 bg-indigo-600 hover:bg-indigo-700">
                <Plus className="h-4 w-4" />
                New Away Period
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>
                  {editMode ? "Edit Away Period" : "Create New Away Period"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="type">Type *</Label>
                    <Select
                      value={form.type}
                      onValueChange={(value) => handleChange("type", value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="break">Break</SelectItem>
                        <SelectItem value="unavailable">Unavailable</SelectItem>
                        <SelectItem value="sick day">Sick Day</SelectItem>
                        <SelectItem value="holiday">Holiday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="repeat">Repeat *</Label>
                    <Select
                      value={form.repeat}
                      onValueChange={(value) => {
                        handleChange("repeat", value);
                        // When changing repeat option, update dates accordingly
                        const currentDate =
                          form.from || new Date().toISOString().split("T")[0];

                        if (value === "no-repeat") {
                          // For no-repeat, set both from and to to current date
                          const startTime = form.startTimeSlot || "09:00";
                          const endTime = form.endTimeSlot || "17:00";
                          handleChange(
                            "from",
                            `${currentDate}T${startTime}:00`,
                          );
                          handleChange("to", `${currentDate}T${endTime}:00`);
                        }
                      }}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no-repeat">No Repeat</SelectItem>
                        <SelectItem value="repeat-daily">
                          Repeat Daily
                        </SelectItem>
                        <SelectItem value="repeat-weekly">
                          Repeat Weekly
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startTimeSlot">Start Time *</Label>
                    <Select
                      value={form.startTimeSlot}
                      onValueChange={(value) => {
                        handleChange("startTimeSlot", value);
                        // Calculate duration based on start and end time
                        const endTime =
                          form.endTimeSlot || addMinutes(value, 60);
                        const duration = minutesBetween(value, endTime);
                        handleChange("duration", duration);
                        handleChange(
                          "from",
                          `${form.from.split("T")[0]}T${value}:00`,
                        );
                      }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Start time" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="endTimeSlot">End Time *</Label>
                    <Select
                      value={form.endTimeSlot}
                      onValueChange={(value) => {
                        handleChange("endTimeSlot", value);
                        // Calculate duration based on start and end time
                        const duration = minutesBetween(
                          form.startTimeSlot,
                          value,
                        );
                        handleChange("duration", duration);
                        handleChange(
                          "to",
                          `${form.to.split("T")[0]}T${value}:00`,
                        );
                      }}>
                      <SelectTrigger>
                        <SelectValue placeholder="End time" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((t) => (
                          <SelectItem
                            key={t}
                            value={t}
                            disabled={t <= form.startTimeSlot}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="duration">Duration</Label>
                    <Input
                      id="duration"
                      type="text"
                      value={`${form.duration} min`}
                      disabled
                      className="bg-gray-50 dark:bg-gray-900"
                    />
                  </div>
                </div>

                {/* Conditionally show date range inputs for repeat options */}
                {form.repeat !== "no-repeat" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="from">Start Date *</Label>
                      <Input
                        id="from"
                        type="date"
                        value={form.from.split("T")[0]}
                        onChange={(e) => {
                          const newFromDate = e.target.value;
                          const startTime = form.startTimeSlot;
                          handleChange(
                            "from",
                            `${newFromDate}T${startTime}:00`,
                          );
                        }}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="to">End Date *</Label>
                      <Input
                        id="to"
                        type="date"
                        value={form.to.split("T")[0]}
                        min={form.from.split("T")[0]}
                        onChange={(e) => {
                          const newToDate = e.target.value;
                          const endTime = form.endTimeSlot;
                          handleChange("to", `${newToDate}T${endTime}:00`);
                        }}
                        required
                      />
                    </div>
                  </div>
                )}

                {/* For no-repeat, show single date input */}
                {form.repeat === "no-repeat" && (
                  <div className="space-y-2">
                    <Label htmlFor="from">Date *</Label>
                    <Input
                      id="from"
                      type="date"
                      value={form.from.split("T")[0]}
                      onChange={(e) => {
                        const newFromDate = e.target.value;
                        const startTime = form.startTimeSlot;
                        const endTime = form.endTimeSlot;
                        handleChange("from", `${newFromDate}T${startTime}:00`);
                        handleChange("to", `${newFromDate}T${endTime}:00`);
                      }}
                      required
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="note">Notes (Optional)</Label>
                  <Textarea
                    id="note"
                    value={form.note}
                    onChange={(e) => handleChange("note", e.target.value)}
                    placeholder="Additional note..."
                    rows={3}
                  />
                </div>

                <div className="text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md">
                  <p>
                    <strong>💡 Tip:</strong>{" "}
                    {form.repeat === "no-repeat"
                      ? "This away time will only apply to the selected date."
                      : form.repeat === "repeat-daily"
                      ? "This away time will repeat every day from the start date to the end date."
                      : "This away time will repeat every week on the same day from the start date to the end date."}
                  </p>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseDialog}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving && (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    )}
                    {editMode ? "Update" : "Create"} Away Period
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <CalendarOff className="h-5 w-5 text-indigo-500" />
            Away Periods ({aways.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
            </div>
          ) : aways.length > 0 ? (
            <div className="space-y-3">
              {aways.map((away) => (
                <div
                  key={away._id}
                  className="border rounded-lg p-4 hover:border-indigo-400 transition">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {getTypeBadge(away.type)}
                        {away.startTimeSlot && away.endTimeSlot && (
                          <Badge variant="outline">
                            {away.startTimeSlot} - {away.endTimeSlot}
                          </Badge>
                        )}
                        {away.repeat && away.repeat !== "no-repeat" && (
                          <Badge variant="secondary">
                            {away.repeat === "repeat-daily"
                              ? "Daily"
                              : away.repeat === "repeat-weekly"
                              ? "Weekly"
                              : away.repeat}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">
                          {format(parseISO(away.from), "PPP")}
                        </span>
                        {" → "}
                        <span className="font-medium">
                          {format(parseISO(away.to), "PPP")}
                        </span>
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Duration: {getDuration(away.from, away.to)}</span>
                        {away.duration && (
                          <span>({away.duration} minutes)</span>
                        )}
                      </div>
                      {away.note && (
                        <p className="text-sm text-gray-500 italic">
                          {away.note}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenDialog(away)}
                        className="gap-1">
                        <Pencil className="h-3 w-3" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(away._id, away.therapistId)}
                        className="gap-1">
                        <Trash2 className="h-3 w-3" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-gray-500">
              <CalendarOff className="h-6 w-6 text-indigo-400" />
              <p>No away periods scheduled. You're available!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AvailabilityManagement;
